import { gql } from '@apollo/client';
import { FULL_CHANNEL } from './pages/queries.tsx';
import {
    BASE_DOCUMENT,
    BASE_FIELD,
    EXTRACTION_SEARCH_RESULTS,
    PRIMITIVE_FIELD,
    SELECT_FIELD_FRAGMENT,
} from '../../document/queries.ts';

const FULL_DELIVERY_NOTE = gql`
    ${SELECT_FIELD_FRAGMENT}
    
    fragment FullDeliveryNote on DeliveryNote {
        ${BASE_DOCUMENT}

        supplier {
            name
            customerNumber
            customerType
            misc
            contactPerson
            ${BASE_FIELD}

            address {
                addressId
                name
                postcode
                city
                country
                phone
                email
                companyName
                streetAndNr
                misc
                bbox
                pageIndex
                predictionConfidence
            }
        }
        
        buyer {
            name
            customerNumber
            customerType
            misc
            contactPerson
            ${BASE_FIELD}

            address {
                addressId
                name
                postcode
                city
                country
                phone
                email
                companyName
                streetAndNr
                misc
                bbox
                pageIndex
                predictionConfidence
            }
        }
        
        deliveryDate {
            ${PRIMITIVE_FIELD}
        }
        deliveryAddress {
            addressId
            addressId2
            name
            postcode
            city
            countrySubdivision
            country
            phone
            email
            companyName
            streetAndNr
            streetName
            streetNumber
            misc
            ${BASE_FIELD}
        }
        deliveryNoteDate {
            ${PRIMITIVE_FIELD}
        }
        deliveryNoteNumber {
            ${PRIMITIVE_FIELD}
        }
        orderDate {
            ${PRIMITIVE_FIELD}
        }
        orderNumber {
            ${PRIMITIVE_FIELD}
        }
        infoText {
            ${PRIMITIVE_FIELD}
        }
        commissionNumber {
            ${PRIMITIVE_FIELD}
        }
        documentType {
            ${PRIMITIVE_FIELD}
        }
        
        lineItems {
            id
            tableId
            parentLineItemId
            
            ${BASE_FIELD}

            pos {
                ${PRIMITIVE_FIELD}
            }
            articleNumber {
                ${PRIMITIVE_FIELD}
            }
            articleNumber2 {
                ${PRIMITIVE_FIELD}
            }
            articleNumber3 {
                ${PRIMITIVE_FIELD}
            }
            clientArticleNumber {
                ${PRIMITIVE_FIELD}
            }
            articlePartition {
                ${PRIMITIVE_FIELD}
            }
            articlePartitionSpecificNumber {
                ${PRIMITIVE_FIELD}
            }
            articleCommissionNumber {
                ${PRIMITIVE_FIELD}
            }
            articleComments {
                ${PRIMITIVE_FIELD}
            }
            articleDescription {
                ${PRIMITIVE_FIELD}
            }
            articleDescriptionMasterData {
                ${PRIMITIVE_FIELD}
            }
            ${EXTRACTION_SEARCH_RESULTS}
            unit {
                ...FullSelectField
            }
            quantity {
                ${PRIMITIVE_FIELD}
            }
            convertedUnit {
                ...FullSelectField
            }
            convertedQuantity {
                ${PRIMITIVE_FIELD}
            }
            orderNumber {
                ${PRIMITIVE_FIELD}
            }
            frameworkContractNumber {
                ${PRIMITIVE_FIELD}
            }
            dynamicSchemaSerialized
        }
        dynamicSchemaSerialized
    }
`;

const FULL_RECORD = gql`
    ${FULL_DELIVERY_NOTE}
    ${FULL_CHANNEL}
    fragment FullRecord on DeliveryNoteProcessingRecord {
        id
        createdAt
        deletedAt
        deletedBy {
            email
            firstName
            lastName
        }
        deletedReason
        emailCount

        sender
        subject

        status

        canFinishAssistance
        canFinishHeaderAssistance
        canFinishLineItemsAssistance

        lockedBy {
            email
        }

        putOnHoldReason
        putOnHoldBy {
            email
            firstName
            lastName
        }

        assistedBy {
            email
            firstName
            lastName
        }

        assistedAt

        createdBy {
            email
            firstName
            lastName
        }

        assignedTo {
            id
            firstName
            lastName
            email
        }

        createdSource

        stepRun {
            id
            stepName
            manualOnly
            executionStatus
            error
            context
        }

        deliverynoteprocessingsteprunSet {
            id
            createdAt
            updatedAt
            stepName
            manualOnly
            executionStatus
            context
            error
        }

        channel {
            ...FullChannel
        }

        isDiscarded
        isTestDocument
        documentType

        discardRecord {
            reason
            user {
                id
                firstName
                lastName
            }
            createdAt
            updatedAt
        }

        deliveryRecord {
            deliveryMethod
            deliveryStatus
            externalReference
            externalMessage
            externalStatus
        }

        sourceFile {
            ... on Email {
                sender {
                    email
                    name
                }
                receivers {
                    email
                    name
                }
                subject
                messageId
                language
                receivedAt

                text
                html
                attachments {
                    cid
                    contentDisposition
                    name
                    type
                    url
                    sourcePartId
                }

                senderClientId
                prediction
                predictionConfidence
                sourceUrl

                predictionConfidences
            }

            ... on File {
                name
                type
                url
            }
        }

        sourceParts {
            id
            fileName
            mimeType
            partType
            fileUrl
            classificationLabel
            classificationConfidence
            recordId
            classificationCorrectedBy
            classificationCorrectedAt
        }

        inputFile {
            ... on File {
                name
                type
                sourceType
            }
        }

        outputFile {
            ... on Export {
                files {
                    name
                    url
                }
            }
        }

        deliveryNoteUnsaved {
            ...FullDeliveryNote
        }

        labelingStatus

        masterdataVersionsExtracted
    }
`;

export const GET_OVERVIEW_DATA = gql`
    query {
        deliveryNoteProcessingChannels {
            id
            name
        }
    }
`;

export const GET_CHANNELS_OVERVIEW_DATA = gql`
    query {
        deliveryNoteProcessingChannels {
            id
            name
            emailAddress
            team {
                id
                users {
                    email
                    firstName
                    lastName
                }
            }
        }
    }
`;

export const GET_TABLE_OVERVIEW_DATA = gql`
    query ($filters: [TableFilter], $orderBy: String, $offset: Int, $first: Int) {
        filteredDeliveryNoteProcessingRecordsList(
            filters: $filters
            orderBy: $orderBy
            offset: $offset
            limit: $first
        ) {
            totalCount
            records {
                id
                sender
                status
                createdAt
                finishedAt
                assistedAt
                assistedBy {
                    id
                    firstName
                    lastName
                    email
                }
                createdBy {
                    email
                    firstName
                    lastName
                }
                assignedTo {
                    id
                    firstName
                    lastName
                    email
                }
                createdSource
                deletedAt
                deletedBy {
                    id
                    firstName
                    lastName
                    email
                }
                deletedReason
                lockedBy {
                    email
                }
                stepRun {
                    id
                    manualOnly
                    executionStatus
                    error
                    context
                }
                channel {
                    id
                    name

                    extractorConfig {
                        validationChecks
                    }
                }
                isDiscarded
                deliveryRecord {
                    deliveryMethod
                    deliveryStatus
                    externalReference
                    externalMessage
                    externalStatus
                }
                discardRecord {
                    reason
                    user {
                        id
                        firstName
                        lastName
                        email
                    }
                    createdAt
                    updatedAt
                }
                isTestDocument
                canProcessAutomatically
                title
                subtitle
                attachmentsCount
                sourcePart {
                    sender {
                        email
                        name
                    }
                }
                emailCount
                putOnHoldReason
                putOnHoldBy {
                    firstName
                    lastName
                }
            }
        }
    }
`;

export const GET_ASSISTANCE_OVERVIEW_DATA = gql`
    query ($recordId: UUID!, $filters: [TableFilter]) {
        deliveryNoteProcessingRecordAssistancePagination(recordId: $recordId, filters: $filters) {
            filteredCount
            position
            previousRecordId
            nextRecordId
        }
    }
`;

export const GET_ASSISTANCE_DATA = gql`
    ${FULL_RECORD}
    ${FULL_DELIVERY_NOTE}
    query ($id: UUID!) {
        deliveryNoteProcessingRecord(id: $id) {
            ...FullRecord

            deliveryNoteExtracted {
                ...FullDeliveryNote
            }

            dusDocument {
                pagesCount
                processedFileUrl
            }

            dusEmail {
                pagesCount
                processedFileUrl
            }
        }
    }
`;

export const UPDATE_PROCESSING_RECORD = gql`
    mutation ($recordId: UUID!, $fieldName: String, $itemIndex: Int, $action: String!, $payload: GenericScalar) {
        deliveryNoteUpdate(
            recordId: $recordId
            fieldName: $fieldName
            itemIndex: $itemIndex
            action: $action
            payload: $payload
        ) {
            record {
                canFinishAssistance
                canFinishHeaderAssistance
                canFinishLineItemsAssistance
                stepRun {
                    id
                    executionStatus
                    manualOnly
                    stepName
                }
            }

            patch {
                op
                path
                from
                value
            }
        }
    }
`;

export const RESELECT_PROCESSING_RECORD = gql`
    mutation (
        $recordId: UUID!
        $fieldName: String
        $bbox: [[Float]]!
        $pageIndex: Int!
        $itemIndex: Int
        $isEmailOcr: Boolean
    ) {
        deliveryNoteReselect(
            recordId: $recordId
            fieldName: $fieldName
            bbox: $bbox
            pageIndex: $pageIndex
            itemIndex: $itemIndex
            isEmailOcr: $isEmailOcr
        ) {
            record {
                canFinishAssistance
                canFinishHeaderAssistance
                canFinishLineItemsAssistance
            }

            patch {
                op
                path
                from
                value
            }
        }
    }
`;

export const DELIVERY_NOTE_EXTRACT_TEXT = gql`
    mutation DeliveryNoteExtractText(
        $recordId: UUID!
        $pageIndex: Int!
        $bbox: [[Float]]!
        $isEmailOcr: Boolean = false
    ) {
        deliveryNoteExtractText(recordId: $recordId, pageIndex: $pageIndex, bbox: $bbox, isEmailOcr: $isEmailOcr) {
            bbox
            text
        }
    }
`;

export const RETRY_STEP = gql`
    mutation ($recordId: UUID!, $stepRunId: UUID!) {
        deliveryNoteRetryStep(recordId: $recordId, stepRunId: $stepRunId) {
            success
        }
    }
`;

export const REOPEN_FOR_ASSISTANCE = gql`
    mutation ($recordId: UUID!, $reason: String) {
        deliveryNoteReopenForAssistance(recordId: $recordId, reason: $reason) {
            success
        }
    }
`;

export const UPLOAD_FILE = gql`
    mutation ($channelId: UUID!, $files: [Upload]!, $batchId: String, $isTestingDocument: Boolean!) {
        deliveryNoteUploadFile(
            input: { channelId: $channelId, files: $files, batchId: $batchId, isTestingDocument: $isTestingDocument }
        ) {
            records {
                id
            }
            success
        }
    }
`;

export const RE_UPLOAD_FILE = gql`
    mutation ($channelId: UUID!, $files: [Upload]!, $recordId: String!, $isTestingDocument: Boolean!) {
        deliveryNoteUploadFile(
            input: { channelId: $channelId, files: $files, recordId: $recordId, isTestingDocument: $isTestingDocument }
        ) {
            records {
                id
            }
            success
        }
    }
`;

export const SEND_TO_LABELING = gql`
    mutation ($recordId: UUID!) {
        deliveryNoteSendToLabeling(recordId: $recordId) {
            success
        }
    }
`;

export const GET_NEXT_ASSISTANCE_RECORD = gql`
    query ($filters: [TableFilter], $channelId: UUID, $orderBy: String) {
        nextDeliveryNoteProcessingRecord(filters: $filters, channelId: $channelId, orderBy: $orderBy) {
            id
        }
    }
`;

export const CREATE_CHANNEL = gql`
    mutation ($name: String!, $fromExistingChannelId: UUID) {
        createDeliveryNoteProcessingChannel(name: $name, fromExistingChannelId: $fromExistingChannelId) {
            channel {
                id
            }
            success
        }
    }
`;

export const GET_ALL_CHANNELS = gql`
    ${FULL_CHANNEL}
    query {
        deliveryNoteProcessingChannels {
            ...FullChannel
        }
    }
`;

export const GET_RELATED_DOCUMENT = gql`
    query ($id: UUID!) {
        deliveryNoteProcessingRecord(id: $id) {
            id
            subject
            createdAt
            finishedAt
            assistedAt
            assistedBy {
                id
                firstName
                lastName
                email
            }
            status
        }
    }
`;
